import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import {
  canSeePortalStart,
  clearToken,
  getStoredToken,
  isRundgangOnlyUser,
  portalHomePath,
  portalSession,
  type PortalUser,
} from "@/lib/kundenportal/auth";

interface Props {
  /** Optional: nur Kunden mit diesem Slug (interne Benutzer dürfen immer) */
  pageSlug?: string;
  /** Nur interne Mitarbeitende erhalten Zugriff. */
  internalOnly?: boolean;
  /** Zusätzlich erlaubt: Konten mit Treffpunkt-Werk-Zugang. */
  allowTreffpunktwerk?: boolean;
  /** Nur das für die Übersichtsseite berechtigte Konto erhält Zugriff. */
  portalStartOnly?: boolean;
  /** Für das Rundgang-Konto ("Althaus") gesperrt. */
  blockBereiche?: boolean;
  children: ReactNode | ((user: PortalUser) => ReactNode);
}

/**
 * Schützt eine Route. Ohne gültige Portal-Sitzung geht es zur Anmeldung,
 * bei falscher Rolle zur kanonischen Startseite des angemeldeten Kontos.
 */
export const PortalGuard = ({
  pageSlug,
  internalOnly = false,
  allowTreffpunktwerk = false,
  portalStartOnly = false,
  blockBereiche = false,
  children,
}: Props) => {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setChecking(false);
      return;
    }
    let active = true;
    portalSession(token)
      .then((res) => {
        if (!active) return;
        if (res.user) setUser(res.user);
        else clearToken();
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-sm text-muted-foreground dark:bg-background">
        Einen Moment bitte …
      </div>
    );
  }

  if (!user) return <Navigate to="/kundenportal" replace />;

  if (blockBereiche && isRundgangOnlyUser(user)) return <Navigate to="/rundgang" replace />;

  const allowed = portalStartOnly
    ? canSeePortalStart(user)
    : internalOnly
      ? user.isInternal || (allowTreffpunktwerk && Boolean(user.isTreffpunktwerk))
      : user.isInternal || !pageSlug || user.pageSlug === pageSlug;

  if (!allowed) return <Navigate to={portalHomePath(user)} replace />;

  return <>{typeof children === "function" ? children(user) : children}</>;
};

export default PortalGuard;

